<template>
  <div>
    <b-card>
      <div class="farms-toolbar">
        <h3 class="farms-toolbar__title mb-0">
          Farms
        </h3>
        <div class="farms-toolbar__actions">
          <b-form-input
            v-model="search"
            placeholder="Search farms..."
            class="farms-toolbar__search"
            @keyup.enter="loadFarms(1)"
          />
          <am-button
            class="farms-toolbar__add"
            icon="PlusIcon"
            label="Add Farm"
            @click="openSetup()"
          />
        </div>
      </div>

      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner />
      </div>

      <div
        v-else-if="!farms.length"
        class="farms-empty text-center py-5"
      >
        <feather-icon
          icon="MapIcon"
          size="42"
          class="text-muted mb-1"
        />
        <h4 class="mb-50">
          No farms yet
        </h4>
        <p class="text-muted mb-2">
          {{ search ? 'No farms match your search.' : 'Register a farm for a farmer to get started.' }}
        </p>
        <am-button
          v-if="!search"
          class="farms-toolbar__add"
          icon="PlusIcon"
          label="Add Farm"
          @click="openSetup()"
        />
        <am-button
          v-else
          variant="outline-secondary"
          label="Clear search"
          @click="clearSearch"
        />
      </div>

      <b-row v-else>
        <b-col
          v-for="farm in farms"
          :key="farm.id"
          md="4"
          class="mb-2"
        >
          <article
            class="farm-card h-100 cursor-pointer"
            @click="$router.push({ name: 'farm-detail', params: { id: farm.id } })"
          >
            <div class="farm-card__top">
              <div class="farm-card__icon">
                🌱
              </div>
              <span
                v-if="farm.crop"
                class="farm-card__badge"
              >{{ farm.crop }}</span>
            </div>

            <h4 class="farm-card__title">
              {{ farm.name }}
            </h4>
            <div class="farm-card__farmer">
              {{ farm.member ? farm.member.full_name : 'Unknown farmer' }}
            </div>

            <div class="farm-card__stats">
              <div class="farm-card__stat">
                <small>Area</small>
                <b>{{ farm.size }} ha</b>
              </div>
              <div class="farm-card__stat">
                <small>Mapped</small>
                <b>{{ farm.farm_mapped ? (farm.mapped_by || 'Yes') : 'No' }}</b>
              </div>
              <div class="farm-card__stat">
                <small>Land</small>
                <b>{{ farm.land_ownership || '—' }}</b>
              </div>
            </div>

            <div class="farm-card__location">
              📍 {{ farm.location }}
            </div>
          </article>
        </b-col>
      </b-row>

      <div
        v-if="farmsTotal > perPage"
        class="d-flex justify-content-end mt-2"
      >
        <b-pagination
          v-model="page"
          :total-rows="farmsTotal"
          :per-page="perPage"
          @change="loadFarms"
        />
      </div>
    </b-card>

    <farm-setup
      :modal-opened="setupOpened"
      :farm="editingFarm"
      :preset-member-id="presetMemberId"
      @modalClosed="setupOpened = false"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormInput, BPagination, BSpinner,
} from 'bootstrap-vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import FarmSetup from '@/views/farm-manager/farms/FarmSetup.vue'
import showToast from '@/farmbusiness/mixins/showToast'

export default {
  name: 'FarmsIndex',
  components: {
    AmButton, FarmSetup, BRow, BCol, BCard, BFormInput, BPagination, BSpinner,
  },
  mixins: [showToast],
  props: {
    memberId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      search: '',
      page: 1,
      perPage: 12,
      loading: false,
      setupOpened: false,
      editingFarm: null,
      presetMemberId: null,
    }
  },
  computed: {
    farms() {
      return this.$store.getters['farmManager/farms']
    },
    farmsTotal() {
      return this.$store.getters['farmManager/farmsTotal']
    },
  },
  mounted() {
    this.presetMemberId = this.memberId || this.$route.query.member_id || null
    this.loadFarms(1)
  },
  methods: {
    loadFarms(page = 1) {
      this.page = page
      this.loading = true
      this.$store.dispatch('farmManager/fetchFarms', {
        search: this.search,
        page: this.page,
        per_page: this.perPage,
        member_id: this.presetMemberId || '',
      }).finally(() => {
        this.loading = false
      })
    },
    clearSearch() {
      this.search = ''
      this.loadFarms(1)
    },
    openSetup(farm = null) {
      this.editingFarm = farm
      this.setupOpened = true
    },
    onSaved() {
      this.setupOpened = false
      this.loadFarms(this.page)
    },
  },
}
</script>

<style scoped>
.farms-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.farms-toolbar__title {
  flex: 0 0 auto;
}

.farms-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 1 auto;
  justify-content: flex-end;
  min-width: 0;
}

.farms-toolbar__search {
  width: 100%;
  max-width: 260px;
  min-width: 160px;
}

.farms-toolbar__add {
  flex: 0 0 auto;
  white-space: nowrap;
}

.farms-toolbar__add >>> .btn,
.farms-toolbar__add >>> div {
  white-space: nowrap;
}

.farms-empty {
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
}

.cursor-pointer {
  cursor: pointer;
}

.farm-card {
  display: flex;
  flex-direction: column;
  padding: 1.1rem 1.15rem 1.15rem;
  border: 1px solid #e4ebe6;
  border-radius: 1rem;
  background:
    linear-gradient(180deg, #f7fbf8 0%, #ffffff 42%);
  box-shadow: 0 1px 2px rgba(4, 196, 49, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.farm-card:hover {
  transform: translateY(-2px);
  border-color: #c9ddd2;
  box-shadow: 0 0.5rem 1.25rem rgba(4, 196, 49, 0.1);
}

.farm-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.farm-card__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #dff3e6;
  color: #04c431;
  display: grid;
  place-items: center;
  font-size: 1.35rem;
  flex: 0 0 auto;
}

.farm-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: #eef2ef;
  color: #405147;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.farm-card__title {
  margin: 0.9rem 0 0.3rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #04c431;
}

.farm-card__farmer {
  color: #6b7c72;
  font-size: 0.9rem;
  margin-bottom: 0.85rem;
}

.farm-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.farm-card__stat {
  background: #f6f8f6;
  border-radius: 0.7rem;
  padding: 0.65rem 0.75rem;
}

.farm-card__stat small {
  display: block;
  color: #7a8a80;
  font-size: 0.72rem;
  margin-bottom: 0.15rem;
}

.farm-card__stat b {
  display: block;
  color: #04c431;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.farm-card__location {
  margin-top: 0.85rem;
  color: #55665d;
  font-size: 0.86rem;
  line-height: 1.35;
}

@media (max-width: 576px) {
  .farms-toolbar__actions {
    width: 100%;
  }

  .farms-toolbar__search {
    max-width: none;
    flex: 1 1 auto;
  }
}
</style>
